import { useEffect } from 'react';
import { Link } from 'react-router-dom';
import Header from '../../components/feature/Header';
import Footer from '../../components/feature/Footer';
import { updatePageSEO, getSiteUrl } from '../../utils/seo';

export default function AboutPage() {
  useEffect(() => {
    const siteUrl = getSiteUrl();
    updatePageSEO({
      title: 'サロン紹介 | おかやま美容室 - 岡山市北区の美容室',
      description: 'おかやま美容室株式会社は2000年の設立以来、岡山市北区を拠点に地域の皆様に愛され続けている美容室です。丁寧なカウンセリングと確かな技術で理想のヘアスタイルを実現。カット、カラー、パーマ、トリートメント、ヘッドスパ、縮毛矯正など幅広いメニューをご用意しています。',
      keywords: '岡山市 美容室, おかやま美容室, サロン紹介, 会社概要, 岡山市北区, 美容室 サービス, カット, カラー, パーマ',
      canonical: `${siteUrl}/about`,
      ogTitle: 'サロン紹介 | おかやま美容室 - 岡山市北区の美容室',
      ogDescription: 'おかやま美容室株式会社は2000年の設立以来、岡山市北区を拠点に地域の皆様に愛され続けている美容室です。',
      ogType: 'website',
      ogUrl: `${siteUrl}/about`,
      ogImage: 'https://public.readdy.ai/ai/img_res/585dd718d8026982a42bb99006081a9b.jpg',
      jsonLd: {
        '@context': 'https://schema.org',
        '@type': 'AboutPage',
        name: 'サロン紹介',
        description: 'おかやま美容室株式会社は2000年の設立以来、岡山市北区を拠点に地域の皆様に愛され続けている美容室です。',
        url: `${siteUrl}/about`,
        mainEntity: {
          '@type': 'Organization',
          name: 'おかやま美容室株式会社',
          foundingDate: '2000',
          address: {
            '@type': 'PostalAddress',
            streetAddress: '○○町1-2-3',
            addressLocality: '岡山市北区',
            addressRegion: '岡山県',
            postalCode: '700-0000',
            addressCountry: 'JP',
          },
          telephone: '+81-86-012-3456',
          faxNumber: '+81-86-012-3457',
          numberOfEmployees: 5,
          founder: {
            '@type': 'Person',
            name: '岡山 太郎',
            jobTitle: '代表取締役',
          },
        },
      },
    });
  }, []);

  return (
    <div className="min-h-screen">
      <Header />
      <main>
        {/* Hero Section */}
        <section className="relative h-96 sm:h-[500px] flex items-center justify-center overflow-hidden">
          <div className="absolute inset-0">
            <img
              src="https://static.readdy.ai/image/2333ec8ce0f23b8d6e87acea74053a0e/2a07fe01b35c2d152a424301230bae88.png"
              alt="おかやま美容室について - 岡山市の美容室"
              className="w-full h-full object-cover object-bottom"
            />
            <div className="absolute inset-0 bg-gradient-to-b from-black/50 via-black/40 to-black/50"></div>
          </div>
          <div className="relative z-10 text-center px-4">
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-white mb-4 tracking-wider" style={{ fontFamily: "'Cormorant Garamond', serif" }}>
              ABOUT
            </h1>
            <p className="text-base sm:text-lg text-white tracking-widest" style={{ fontFamily: "'Montserrat', sans-serif" }}>
              サロン紹介
            </p>
          </div>
        </section>

        {/* Company Info */}
        <section className="py-16 sm:py-20 lg:py-24 bg-white">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-12 items-center mb-16 sm:mb-20">
              <div>
                <h2 className="text-3xl sm:text-4xl font-bold mb-6 tracking-wider" style={{ fontFamily: "'Cormorant Garamond', serif" }}>
                  あなたらしい美しさを<br />もっと輝かせる
                </h2>
                <p className="text-sm sm:text-base text-gray-700 leading-relaxed mb-6">
                  おかやま美容室株式会社は、2000年の設立以来、岡山市北区を拠点に地域の皆様に愛され続けている美容室です。「お客様一人ひとりの個性を大切にし、その魅力を最大限に引き出す」という理念のもと、丁寧なカウンセリングと確かな技術で、理想のヘアスタイルを実現してまいりました。
                </p>
                <p className="text-sm sm:text-base text-gray-700 leading-relaxed mb-6">
                  経験豊富なスタイリストが、最新のトレンドと伝統的な技術を融合させ、お客様のライフスタイルや髪質、顔立ちに合わせた最適なスタイルをご提案いたします。岡山市でヘアサロンをお探しの方、髪のお悩みをお持ちの方は、ぜひ一度ご来店ください。
                </p>
                <p className="text-sm sm:text-base text-gray-700 leading-relaxed">
                  私たちは、技術力の向上はもちろん、お客様とのコミュニケーションを大切にし、リラックスして施術を受けていただける空間づくりにも力を入れています。あなたらしい美しさを、もっと輝かせることが私たちの使命です。
                </p>
              </div>
              <div className="relative h-96 sm:h-[500px] rounded-lg overflow-hidden shadow-xl">
                <img
                  src="https://static.readdy.ai/image/2333ec8ce0f23b8d6e87acea74053a0e/a2c9627c63a6a1b2c1c2562271b6da3b.jpeg"
                  alt="おかやま美容室のスタッフ - 岡山市の美容室"
                  className="w-full h-full object-cover object-top"
                />
              </div>
            </div>

            {/* Services */}
            <div className="mb-16 sm:mb-20">
              <h3 className="text-2xl sm:text-3xl font-bold mb-8 sm:mb-12 text-center tracking-wider" style={{ fontFamily: "'Cormorant Garamond', serif" }}>
                サービス内容
              </h3>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 sm:gap-8">
                <div className="bg-gradient-to-br from-amber-50 to-orange-50 p-6 sm:p-8 rounded-lg">
                  <div className="w-12 h-12 sm:w-14 sm:h-14 bg-white rounded-full flex items-center justify-center mb-4 sm:mb-6 shadow-md">
                    <i className="ri-scissors-cut-line text-2xl sm:text-3xl text-gray-900 w-8 h-8 flex items-center justify-center"></i>
                  </div>
                  <h4 className="text-lg sm:text-xl font-bold mb-3 tracking-wider" style={{ fontFamily: "'Noto Sans JP', sans-serif" }}>
                    カット
                  </h4>
                  <p className="text-sm text-gray-700 leading-relaxed">
                    お客様の骨格や髪質を見極め、最適なカット技術で理想のスタイルを実現します。トレンドを取り入れながらも、扱いやすさを重視した施術を行います。
                  </p>
                </div>

                <div className="bg-gradient-to-br from-rose-50 to-pink-50 p-6 sm:p-8 rounded-lg">
                  <div className="w-12 h-12 sm:w-14 sm:h-14 bg-white rounded-full flex items-center justify-center mb-4 sm:mb-6 shadow-md">
                    <i className="ri-palette-line text-2xl sm:text-3xl text-gray-900 w-8 h-8 flex items-center justify-center"></i>
                  </div>
                  <h4 className="text-lg sm:text-xl font-bold mb-3 tracking-wider" style={{ fontFamily: "'Noto Sans JP', sans-serif" }}>
                    カラー
                  </h4>
                  <p className="text-sm text-gray-700 leading-relaxed">
                    髪と頭皮に優しい高品質なカラー剤を使用し、お客様のイメージに合わせた色味をご提案します。ダメージを最小限に抑えた施術を心がけています。
                  </p>
                </div>

                <div className="bg-gradient-to-br from-amber-50 to-yellow-50 p-6 sm:p-8 rounded-lg">
                  <div className="w-12 h-12 sm:w-14 sm:h-14 bg-white rounded-full flex items-center justify-center mb-4 sm:mb-6 shadow-md">
                    <i className="ri-contrast-drop-line text-2xl sm:text-3xl text-gray-900 w-8 h-8 flex items-center justify-center"></i>
                  </div>
                  <h4 className="text-lg sm:text-xl font-bold mb-3 tracking-wider" style={{ fontFamily: "'Noto Sans JP', sans-serif" }}>
                    パーマ
                  </h4>
                  <p className="text-sm text-gray-700 leading-relaxed">
                    自然な動きとボリュームを演出するパーマ施術。髪質に合わせた薬剤選定で、美しいカールを長持ちさせます。
                  </p>
                </div>

                <div className="bg-gradient-to-br from-rose-50 to-orange-50 p-6 sm:p-8 rounded-lg">
                  <div className="w-12 h-12 sm:w-14 sm:h-14 bg-white rounded-full flex items-center justify-center mb-4 sm:mb-6 shadow-md">
                    <i className="ri-leaf-line text-2xl sm:text-3xl text-gray-900 w-8 h-8 flex items-center justify-center"></i>
                  </div>
                  <h4 className="text-lg sm:text-xl font-bold mb-3 tracking-wider" style={{ fontFamily: "'Noto Sans JP', sans-serif" }}>
                    トリートメント
                  </h4>
                  <p className="text-sm text-gray-700 leading-relaxed">
                    髪の内部から補修する集中トリートメント。ダメージヘアを健康的な美髪へと導きます。定期的なケアで美しい髪を維持できます。
                  </p>
                </div>

                <div className="bg-gradient-to-br from-amber-50 to-rose-50 p-6 sm:p-8 rounded-lg">
                  <div className="w-12 h-12 sm:w-14 sm:h-14 bg-white rounded-full flex items-center justify-center mb-4 sm:mb-6 shadow-md">
                    <i className="ri-moon-clear-line text-2xl sm:text-3xl text-gray-900 w-8 h-8 flex items-center justify-center"></i>
                  </div>
                  <h4 className="text-lg sm:text-xl font-bold mb-3 tracking-wider" style={{ fontFamily: "'Noto Sans JP', sans-serif" }}>
                    ヘッドスパ
                  </h4>
                  <p className="text-sm text-gray-700 leading-relaxed">
                    頭皮の血行を促進し、リラクゼーション効果も高いヘッドスパ。健康な髪を育てる土台づくりをサポートします。
                  </p>
                </div>

                <div className="bg-gradient-to-br from-pink-50 to-orange-50 p-6 sm:p-8 rounded-lg">
                  <div className="w-12 h-12 sm:w-14 sm:h-14 bg-white rounded-full flex items-center justify-center mb-4 sm:mb-6 shadow-md">
                    <i className="ri-magic-line text-2xl sm:text-3xl text-gray-900 w-8 h-8 flex items-center justify-center"></i>
                  </div>
                  <h4 className="text-lg sm:text-xl font-bold mb-3 tracking-wider" style={{ fontFamily: "'Noto Sans JP', sans-serif" }}>
                    縮毛矯正
                  </h4>
                  <p className="text-sm text-gray-700 leading-relaxed">
                    クセやうねりを自然なストレートヘアに。髪質改善効果も期待できる、ダメージレスな縮毛矯正を提供します。
                  </p>
                </div>
              </div>
            </div>

            {/* Company Details */}
            <div className="mb-16 sm:mb-20">
              <h3 className="text-2xl sm:text-3xl font-bold mb-8 sm:mb-12 text-center tracking-wider" style={{ fontFamily: "'Cormorant Garamond', serif" }}>
                アクセスマップ
              </h3>
              <div className="w-full h-96 sm:h-[500px] rounded-lg overflow-hidden shadow-xl">
                <iframe
                  src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3281.234567890123!2d133.9191!3d34.6617!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x0%3A0x0!2zMzTCsDM5JzQyLjEiTiAxMzPCsDU1JzA4LjgiRQ!5e0!3m2!1sja!2sjp!4v1234567890123!5m2!1sja!2sjp"
                  width="100%"
                  height="100%"
                  style={{ border: 0 }}
                  allowFullScreen
                  loading="lazy"
                  referrerPolicy="no-referrer-when-downgrade"
                  title="おかやま美容室 アクセスマップ"
                ></iframe>
              </div>
            </div>

            <div className="bg-gradient-to-br from-amber-50 via-orange-50 to-rose-50 rounded-lg p-8 sm:p-12">
              <h3 className="text-2xl sm:text-3xl font-bold mb-8 sm:mb-12 text-center tracking-wider" style={{ fontFamily: "'Cormorant Garamond', serif" }}>
                会社概要
              </h3>
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-12 items-center">
                <div className="relative h-96 sm:h-[500px] rounded-lg overflow-hidden shadow-xl">
                  <img
                    src="https://static.readdy.ai/image/2333ec8ce0f23b8d6e87acea74053a0e/7a2e2b0f6ef36347534ad72b1a20cb1c.jpeg"
                    alt="おかやま美容室 - 岡山市の美容室"
                    className="w-full h-full object-cover object-top"
                  />
                </div>
                <div>
                  <dl className="space-y-4 sm:space-y-6">
                    <div className="grid grid-cols-1 sm:grid-cols-3 gap-2 sm:gap-4 pb-4 sm:pb-6 border-b border-gray-300">
                      <dt className="text-sm sm:text-base font-semibold text-gray-900">会社名</dt>
                      <dd className="text-sm sm:text-base text-gray-700 sm:col-span-2">おかやま美容室株式会社</dd>
                    </div>
                    <div className="grid grid-cols-1 sm:grid-cols-3 gap-2 sm:gap-4 pb-4 sm:pb-6 border-b border-gray-300">
                      <dt className="text-sm sm:text-base font-semibold text-gray-900">代表取締役</dt>
                      <dd className="text-sm sm:text-base text-gray-700 sm:col-span-2">岡山 太郎</dd>
                    </div>
                    <div className="grid grid-cols-1 sm:grid-cols-3 gap-2 sm:gap-4 pb-4 sm:pb-6 border-b border-gray-300">
                      <dt className="text-sm sm:text-base font-semibold text-gray-900">設立</dt>
                      <dd className="text-sm sm:text-base text-gray-700 sm:col-span-2">2000年</dd>
                    </div>
                    <div className="grid grid-cols-1 sm:grid-cols-3 gap-2 sm:gap-4 pb-4 sm:pb-6 border-b border-gray-300">
                      <dt className="text-sm sm:text-base font-semibold text-gray-900">従業員数</dt>
                      <dd className="text-sm sm:text-base text-gray-700 sm:col-span-2">5名</dd>
                    </div>
                    <div className="grid grid-cols-1 sm:grid-cols-3 gap-2 sm:gap-4 pb-4 sm:pb-6 border-b border-gray-300">
                      <dt className="text-sm sm:text-base font-semibold text-gray-900">所在地</dt>
                      <dd className="text-sm sm:text-base text-gray-700 sm:col-span-2">〒700-0000 岡山市北区○○町1-2-3</dd>
                    </div>
                    <div className="grid grid-cols-1 sm:grid-cols-3 gap-2 sm:gap-4 pb-4 sm:pb-6 border-b border-gray-300">
                      <dt className="text-sm sm:text-base font-semibold text-gray-900">電話番号</dt>
                      <dd className="text-sm sm:text-base text-gray-700 sm:col-span-2">086-012-3456</dd>
                    </div>
                    <div className="grid grid-cols-1 sm:grid-cols-3 gap-2 sm:gap-4 pb-4 sm:pb-6 border-b border-gray-300">
                      <dt className="text-sm sm:text-base font-semibold text-gray-900">FAX</dt>
                      <dd className="text-sm sm:text-base text-gray-700 sm:col-span-2">086-012-3457</dd>
                    </div>
                    <div className="grid grid-cols-1 sm:grid-cols-3 gap-2 sm:gap-4 pb-4 sm:pb-6 border-b border-gray-300">
                      <dt className="text-sm sm:text-base font-semibold text-gray-900">営業時間</dt>
                      <dd className="text-sm sm:text-base text-gray-700 sm:col-span-2">9:00 - 18:00</dd>
                    </div>
                    <div className="grid grid-cols-1 sm:grid-cols-3 gap-2 sm:gap-4">
                      <dt className="text-sm sm:text-base font-semibold text-gray-900">定休日</dt>
                      <dd className="text-sm sm:text-base text-gray-700 sm:col-span-2">日曜日・祝日</dd>
                    </div>
                  </dl>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* CTA */}
        <section className="py-16 sm:py-20 bg-white">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
            <h2 className="text-3xl sm:text-4xl font-bold mb-6 tracking-wider" style={{ fontFamily: "'Cormorant Garamond', serif" }}>
              ご予約・お問い合わせ
            </h2>
            <p className="text-sm sm:text-base text-gray-700 leading-relaxed mb-8 sm:mb-12">
              岡山市でヘアサロンをお探しの方は、お気軽にお問い合わせください。
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <a
                href="tel:086-012-3456"
                className="inline-flex items-center px-8 py-4 bg-gray-900 text-white text-sm font-semibold rounded-full hover:bg-gray-800 transition-all duration-300 whitespace-nowrap cursor-pointer justify-center"
                style={{ fontFamily: "'Montserrat', sans-serif" }}
              >
                <i className="ri-phone-line mr-2 w-5 h-5 flex items-center justify-center"></i>
                086-012-3456
              </a>
              <Link
                to="/contact"
                className="inline-flex items-center px-8 py-4 bg-transparent border-2 border-gray-900 text-gray-900 text-sm font-semibold rounded-full hover:bg-gray-900 hover:text-white transition-all duration-300 whitespace-nowrap cursor-pointer justify-center"
                style={{ fontFamily: "'Montserrat', sans-serif" }}
              >
                <i className="ri-mail-line mr-2 w-5 h-5 flex items-center justify-center"></i>
                お問い合わせ
              </Link>
            </div>
          </div>
        </section>
      </main>
      <Footer />
    </div>
  );
}
